import { useState, useEffect } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';
import { uploadFile } from '../lib/fileUpload';

const G = BRAND.colors.primary;

const SHIPPING_OPTIONS = ['Ground', '2-Day', 'Overnight', 'Customer Pickup', 'LTL Freight', 'Other'];

// ─── CreatePOFlow ─────────────────────────────────────────────────────────────
// Modal-style flow that takes a presentation_id and walks the distributor
// through creating one PO per supplier, with per-product decorations.
//
// Three steps:
//   1) Loading drafts (auto-grouped by supplier)
//   2) Review/edit ship-to, in-hand date, shipping method, proof, then
//      per-PO: decoration locations + colors + mockup filenames per product.
//   3) Confirm → POs are created.
export default function CreatePOFlow({
  presentationId,
  itemsOverride,
  clientName: initialClientName,
  clientId,
  getToken,
  onClose,
  onCreated,
  showToast,
}) {
  const [step, setStep] = useState('loading');
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [distributorInfo, setDistributorInfo] = useState({});
  const [presentationName, setPresentationName] = useState(null);
  const [clientName, setClientName] = useState(initialClientName || '');

  // PO-level fields
  const [shipTo, setShipTo] = useState({
    name: '', company: '', address1: '', address2: '',
    city: '', state: '', zip: '', phone: '',
  });
  const [inHandDate, setInHandDate] = useState('');
  const [terms, setTerms] = useState('');
  const [proofRequired, setProofRequired] = useState(true);
  const [shippingChoice, setShippingChoice] = useState('Ground');
  const [shippingOther, setShippingOther] = useState('');
  // "Ship on our account" — '' means vendor ships & bills us (default).
  const [shipAccountCarrier, setShipAccountCarrier] = useState('');
  const [externalNotes, setExternalNotes] = useState('');

  // Client addresses (Push C2). When this PO is linked to a client, we fetch
  // their saved addresses. The default one prepopulates shipTo; if multiple
  // exist, we show a picker. If the user types a one-off address that doesn't
  // match anything saved, they can opt-in to save it back to the client.
  const [clientAddresses, setClientAddresses]   = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);  // 'new' | uuid | null
  const [saveAddressBack, setSaveAddressBack]     = useState(false);
  // The client's own profile fields (company_name, contact_name, phone) —
  // separate from client_addresses, which don't carry a company field at all.
  // A saved address supplies the street; the client record supplies who
  // they are. Used to prefill Company always, and Name/Phone when no address
  // exists yet to pull them from.
  const [clientProfile, setClientProfile] = useState(null);
  // Reasons a decoration job silently failed to become a PO line (e.g. its
  // matrix has no price for the job's #colors/qty combination) — surfaced so
  // a missing PO has an explanation instead of just not appearing.
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const body = presentationId ? { presentation_id: presentationId } : { items: itemsOverride || [] };
        const data = await api.withToken(token).previewPOs(body);
        setDrafts(data.drafts || []);
        setWarnings(data.warnings || []);
        setDistributorInfo(data.distributor_info || {});
        if (data.presentation_name) setPresentationName(data.presentation_name);
        if (data.client_name && !clientName) setClientName(data.client_name);
        setTerms(data.distributor_info?.terms_default || 'Net 30');

        // If the PO is linked to a client (via presentation.client_id or
        // explicit clientId prop), pull their profile (company/contact/phone —
        // none of which live on client_addresses) and their saved addresses.
        if (clientId) {
          let clientRec = null;
          try {
            const cres = await api.withToken(token).getClient(clientId);
            clientRec = cres?.client || null;
            setClientProfile(clientRec);
          } catch { /* non-blocking */ }
          try {
            const addrs = await api.withToken(token).listClientAddresses(clientId);
            setClientAddresses(addrs);
            const def = addrs.find(a => a.is_default) || addrs[0];
            if (def) {
              setSelectedAddressId(def.id);
              applyAddressToShipTo(def, data.client_name, clientRec);
            } else {
              // No saved address yet — still seed name/company/phone from the
              // client's own profile so a rep isn't retyping what's already
              // on file, even though there's no address to pick from.
              seedShipToFromProfile(clientRec, data.client_name);
            }
          } catch { /* non-blocking */ }
        }

        if (!data.drafts || data.drafts.length === 0) {
          setError('No items to create POs for.');
          setStep('error');
        } else {
          setStep('review');
        }
      } catch (e) {
        const msg = e?.message || 'Failed to load draft POs';
        if (msg.includes('PO account info not configured')) {
          setError('PO account info not set up yet. Visit Settings → Purchase Orders first.');
        } else {
          setError(msg);
        }
        setStep('error');
      }
    })();
    // eslint-disable-next-line
  }, [presentationId]);

  // Map a client_addresses row onto the shipTo shape used by the PO form.
  // The ship_to_contact_name on the address takes priority; otherwise we fall
  // back to the client's contact_name, then the presentation's client_name
  // snapshot. Company never lived on the address row — it comes from the
  // client record's own company_name field.
  function applyAddressToShipTo(addr, fallbackContactName, clientRec) {
    setShipTo({
      name:     addr.ship_to_contact_name || clientRec?.contact_name || fallbackContactName || '',
      company:  clientRec?.company_name || '',
      address1: addr.line1 || '',
      address2: addr.line2 || '',
      city:     addr.city || '',
      state:    addr.state || '',
      zip:      addr.postal_code || '',
      phone:    addr.ship_to_phone || clientRec?.phone || '',
    });
  }

  // No saved address to build a ship-to from yet — still worth seeding
  // name/company/phone from the client's own profile so a rep isn't retyping
  // what's already on file. Only touches blank fields.
  function seedShipToFromProfile(clientRec, fallbackContactName) {
    if (!clientRec && !fallbackContactName) return;
    setShipTo(s => ({
      ...s,
      name: s.name || clientRec?.contact_name || fallbackContactName || '',
      company: s.company || clientRec?.company_name || '',
      phone: s.phone || clientRec?.phone || '',
    }));
  }

  const handleAddressPick = (id) => {
    setSelectedAddressId(id);
    if (id === 'new') {
      // Clear ship-to so they can type fresh. Pre-fill name/company/phone
      // from the client's own profile, same as the no-saved-address case.
      setShipTo({
        name: clientProfile?.contact_name || clientName, company: clientProfile?.company_name || '',
        address1: '', address2: '',
        city: '', state: '', zip: '', phone: clientProfile?.phone || '',
      });
      return;
    }
    const addr = clientAddresses.find(a => a.id === id);
    if (addr) applyAddressToShipTo(addr, clientName, clientProfile);
  };

  const updateDraft = (supplierCode, patch) => {
    setDrafts(prev => prev.map(d => d.supplier_code === supplierCode ? { ...d, ...patch } : d));
  };

  const removeDraft = (supplierCode) => {
    setDrafts(prev => prev.filter(d => d.supplier_code !== supplierCode));
  };

  const validate = () => {
    if (!shipTo.address1.trim()) return 'Ship-to address line 1 is required';
    if (!shipTo.city.trim()) return 'Ship-to city is required';
    if (!shipTo.state.trim()) return 'Ship-to state is required';
    if (!shipTo.zip.trim()) return 'Ship-to ZIP is required';
    if (drafts.length === 0) return 'No POs to create';
    if (shippingChoice === 'Other' && !shippingOther.trim()) return 'Please specify the shipping method';
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setStep('submitting');
    try {
      const token = await getToken();
      const shipping = shippingChoice === 'Other' ? shippingOther.trim() : shippingChoice;
      // Optional: save this one-off address back to the client. Fire-and-forget
      // — failure here shouldn't block PO creation.
      if (clientId && selectedAddressId === 'new' && saveAddressBack && shipTo.address1.trim()) {
        api.withToken(token).createClientAddress(clientId, {
          label: '',
          ship_to_contact_name: shipTo.name || null,
          ship_to_phone: shipTo.phone || null,
          line1: shipTo.address1,
          line2: shipTo.address2 || null,
          city: shipTo.city,
          state: shipTo.state,
          postal_code: shipTo.zip,
          country: 'US',
          is_default: false,  // user-typed one-offs don't become default
        }).catch(() => { /* non-blocking */ });
      }
      const result = await api.withToken(token).createPOs({
        presentation_id: presentationId || null,
        ship_to: shipTo,
        in_hand_date: inHandDate || null,
        terms,
        client_name: clientName || null,
        proof_required: proofRequired,
        shipping_method: shipping,
        ship_account_carrier: shipAccountCarrier || null,
        external_notes: externalNotes.trim() || null,
        drafts,
      });
      showToast?.(`Created ${result.pos.length} PO${result.pos.length === 1 ? '' : 's'}`, 'success');
      onCreated?.(result.pos);
    } catch (e) {
      setError(e?.message || 'Failed to create POs');
      setStep('review');
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>Create Purchase Orders</div>
            {presentationName && (
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                From: {presentationName}{clientName ? ` · ${clientName}` : ''}
              </div>
            )}
          </div>
          <button onClick={onClose} style={closeBtn} aria-label="Close">×</button>
        </div>

        <div style={modalBody}>
          {step === 'submitting' && (
            <div style={{ position:'fixed', inset:0, background:'rgba(255,255,255,0.94)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', zIndex:1000, gap:14 }}>
              <div style={{ width:40, height:40, border:'3px solid #e2e8f0', borderTopColor:'#059669', borderRadius:'50%', animation:'po-spin 0.8s linear infinite' }}/>
              <div style={{ fontSize:16, fontWeight:600, color:'#0f172a' }}>Creating your purchase orders…</div>
              <div style={{ fontSize:12, color:'#64748b' }}>Generating {drafts.length} PO{drafts.length===1?'':'s'} — this can take a few seconds.</div>
              <style>{`@keyframes po-spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          )}
          {step === 'loading' && (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              Grouping items by supplier…
            </div>
          )}

          {step === 'error' && (
            <div style={{ padding: 24 }}>
              <div style={errorBox}>{error}</div>
              <button onClick={onClose} style={{ ...btnSecondary, marginTop: 16 }}>Close</button>
            </div>
          )}

          {(step === 'review' || step === 'submitting') && (
            <>
              {error && <div style={errorBox}>{error}</div>}

              <div style={{
                background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8,
                padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#075985',
              }}>
                <strong>{drafts.length}</strong> PO{drafts.length === 1 ? '' : 's'} will be created
                {drafts.length > 1 ? ' (one per supplier)' : ''}.
                Total: <strong>${drafts.reduce((s, d) => s + (d.total || 0), 0).toFixed(2)}</strong>
              </div>

              <SectionHeader title="Ship to" />
              {/* Address picker — only when this PO is linked to a client with
                  saved addresses. Lets the distributor switch between saved
                  addresses or pick "Type a new one" to enter free-form. */}
              {clientAddresses.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={labelStyle}>Saved address for {clientName || 'this client'}</div>
                  <select
                    value={selectedAddressId || ''}
                    onChange={e => handleAddressPick(e.target.value)}
                    style={{ ...inputStyle, padding: '8px 10px' }}>
                    {clientAddresses.map(a => (
                      <option key={a.id} value={a.id}>
                        {(a.label || 'Address')}{a.is_default ? ' (default)' : ''} — {[a.line1, a.city, a.state].filter(Boolean).join(', ')}
                      </option>
                    ))}
                    <option value="new">— Type a new address —</option>
                  </select>
                </div>
              )}
              <div style={gridTwo}>
                <Field label="Recipient name" value={shipTo.name} onChange={v => setShipTo({...shipTo, name: v})} />
                <Field label="Company" value={shipTo.company} onChange={v => setShipTo({...shipTo, company: v})} />
                <Field label="Address line 1 *" value={shipTo.address1} onChange={v => setShipTo({...shipTo, address1: v})} fullWidth />
                <Field label="Address line 2" value={shipTo.address2} onChange={v => setShipTo({...shipTo, address2: v})} fullWidth />
                <Field label="City *" value={shipTo.city} onChange={v => setShipTo({...shipTo, city: v})} />
                <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 8 }}>
                  <Field label="State *" maxLength={2} value={shipTo.state} onChange={v => setShipTo({...shipTo, state: v.toUpperCase()})} />
                  <Field label="ZIP *" value={shipTo.zip} onChange={v => setShipTo({...shipTo, zip: v})} />
                </div>
                <Field label="Phone" value={shipTo.phone} onChange={v => setShipTo({...shipTo, phone: v})} />
                <div />
              </div>
              {/* Offer to save this address back to the client. Only shows when:
                  - the PO is linked to a client (so we have somewhere to save), and
                  - the user is typing a new one-off (not picking a saved address).
                  Saves automatically as part of the create-PO request below. */}
              {clientId && selectedAddressId === 'new' && shipTo.address1.trim() && (
                <label style={{ display:'flex',alignItems:'center',gap:8,marginTop:8,fontSize:12,color:'#374151',cursor:'pointer' }}>
                  <input type="checkbox" checked={saveAddressBack}
                    onChange={e => setSaveAddressBack(e.target.checked)}/>
                  Save this address to {clientName || 'client'} for future POs
                </label>
              )}

              <SectionHeader title="Order details" />
              <div style={gridTwo}>
                <Field label="In-hand by" type="date" value={inHandDate} onChange={setInHandDate} />
                <Field label="Payment terms" value={terms} onChange={setTerms} />
              </div>

              <SectionHeader title="Shipping & proof" />
              <div style={gridTwo}>
                <div>
                  <div style={labelStyle}>Shipping method</div>
                  <select
                    value={shippingChoice}
                    onChange={e => setShippingChoice(e.target.value)}
                    style={{ ...inputStyle, padding: '8px 10px' }}
                  >
                    {SHIPPING_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  {shippingChoice === 'Other' && (
                    <input
                      placeholder="Specify shipping method"
                      value={shippingOther}
                      onChange={e => setShippingOther(e.target.value)}
                      style={{ ...inputStyle, marginTop: 6 }}
                    />
                  )}
                  {(() => {
                    const carriers = distributorInfo?.carrier_accounts || {};
                    const configured = ['ups', 'fedex'].filter(
                      c => carriers?.[c]?.account_number && carriers?.[c]?.zip
                    );
                    if (configured.length === 0) return null;
                    const LABEL = { ups: 'UPS', fedex: 'FedEx' };
                    return (
                      <div style={{ marginTop: 8 }}>
                        <div style={labelStyle}>Ship on our account</div>
                        <select
                          value={shipAccountCarrier}
                          onChange={e => setShipAccountCarrier(e.target.value)}
                          style={{ ...inputStyle, padding: '8px 10px' }}
                        >
                          <option value="">Vendor ships &amp; bills us</option>
                          {configured.map(c => (
                            <option key={c} value={c}>{LABEL[c]} — bill freight to our account</option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <div style={labelStyle}>Proof requirement</div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={proofRequired}
                      onChange={e => setProofRequired(e.target.checked)}
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: 13, color: '#334155' }}>
                      Proof required before production
                    </span>
                  </label>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    {proofRequired ? 'Supplier will send a proof for approval' : 'No proof needed — production can begin immediately'}
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={labelStyle}>Notes to supplier (prints on the PO)</div>
                <textarea
                  value={externalNotes}
                  onChange={e => setExternalNotes(e.target.value)}
                  rows={3}
                  placeholder="e.g. Match PMS 286C on the left chest. Ship no later than Friday."
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                  Visible to the supplier on the PO. For private notes, use the internal notes field after creating the PO.
                </div>
              </div>

              <SectionHeader title={`POs to create (${drafts.length})`} />
              <div style={{
                background: '#fefce8', border: '1px solid #fde047', borderRadius: 6,
                padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#713f12',
              }}>
                💡 Attach artwork files directly below — decorator POs at the art level, supplier PO decoration locations at the item level.
              </div>
              {warnings.length > 0 && (
                <div style={{
                  background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 6,
                  padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#991B1B',
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {warnings.length} job{warnings.length === 1 ? '' : 's'} couldn't be turned into a PO line:
                  </div>
                  {warnings.map((w, i) => (
                    <div key={i} style={{ marginTop: i > 0 ? 4 : 0 }}>• {w}</div>
                  ))}
                </div>
              )}
              {drafts.map(draft => (
                <DraftCard
                  key={draft.supplier_code}
                  draft={draft}
                  onChange={(patch) => updateDraft(draft.supplier_code, patch)}
                  onRemove={() => removeDraft(draft.supplier_code)}
                  getToken={getToken} showToast={showToast} clientId={clientId}
                />
              ))}

              <div style={{
                display: 'flex', justifyContent: 'flex-end', gap: 10,
                marginTop: 24, paddingTop: 20, borderTop: '1px solid #e2e8f0',
              }}>
                <button onClick={onClose} style={btnSecondary} disabled={step === 'submitting'}>
                  Cancel
                </button>
                <button
                  onClick={submit}
                  style={btnPrimary}
                  disabled={step === 'submitting' || drafts.length === 0}
                >
                  {step === 'submitting' ? 'Creating…' : `Create ${drafts.length} PO${drafts.length === 1 ? '' : 's'}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── DraftCard ────────────────────────────────────────────────────────────────
function DraftCard({ draft, onChange, onRemove, getToken, showToast, clientId }) {
  const [expanded, setExpanded] = useState(true);

  const updateItem = (idx, patch) => {
    onChange({ items: draft.items.map((it, i) => i === idx ? { ...it, ...patch } : it) });
  };

  const addCharge = () => {
    onChange({ charges: [...(draft.charges || []), { label: '', amount: 0 }] });
  };
  const updateCharge = (idx, patch) => {
    const next = [...(draft.charges || [])];
    next[idx] = { ...next[idx], ...patch };
    onChange({ charges: next });
  };
  const removeCharge = (idx) => {
    onChange({ charges: (draft.charges || []).filter((_, i) => i !== idx) });
  };

  const productsTotal = draft.kind === 'decorator'
    ? (Number(draft.products_subtotal) || 0)
    : (draft.items || []).reduce((s, i) => s + (Number(i.line_total) || 0), 0);
  const chargesTotal = (draft.charges || []).reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const total = productsTotal + chargesTotal;

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 10, background: '#fff' }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', cursor: 'pointer', gap: 12 }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{draft.supplier_name}{draft.kind === 'decorator' ? ' · Decorator' : ''}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            {draft.kind === 'decorator'
              ? `${(draft.deco_items || []).length} imprint${(draft.deco_items || []).length === 1 ? '' : 's'}`
              : `${draft.items.length} item${draft.items.length === 1 ? '' : 's'}`}
            {draft.account_number && ` · Account ${draft.account_number}`}
          </div>
        </div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>${total.toFixed(2)}</div>
        <div style={{ fontSize: 18, color: '#94a3b8', transform: expanded ? 'rotate(180deg)' : 'none' }}>⌄</div>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid #e2e8f0', padding: 16 }}>
          {/* Account number (not needed on a decorator PO) */}
          {draft.kind !== 'decorator' && (
            <Field
              label={`Distributor account # at ${draft.supplier_name}`}
              value={draft.account_number || ''}
              onChange={v => onChange({ account_number: v })}
            />
          )}

          {draft.kind === 'decorator' ? (
            <DecoratorItems draft={draft} onChange={onChange} getToken={getToken} showToast={showToast} clientId={clientId} />
          ) : (
            /* Per-item editors */
            <div style={{ marginTop: 16 }}>
              <div style={{ ...labelStyle, marginBottom: 10 }}>Items in this PO</div>
              {draft.items.map((item, idx) => (
                <ItemEditor
                  key={idx}
                  item={item}
                  onChange={(patch) => updateItem(idx, patch)}
                  getToken={getToken} showToast={showToast} clientId={clientId}
                />
              ))}
            </div>
          )}

          {/* Supporting documents — proofs, spec sheets, reference files that
              apply to the whole PO, not one location/art. Distinct from the
              per-art (decorator) or per-location (supplier) attach controls
              above: this is a plain list, add as many as needed. */}
          <SupportingDocs
            files={draft.supporting_files || []}
            onChange={(next) => onChange({ supporting_files: next })}
            getToken={getToken} showToast={showToast} clientId={clientId}
          />

          {/* Charges */}
          <div style={{ marginTop: 14 }}>
            <div style={labelStyle}>Additional charges</div>
            {(draft.charges || []).map((c, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <input
                  placeholder="Label (e.g. Setup, Rush, Shipping)"
                  value={c.label || ''}
                  onChange={e => updateCharge(idx, { label: e.target.value })}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={c.amount || ''}
                  onChange={e => updateCharge(idx, { amount: Number(e.target.value) || 0 })}
                  style={{ ...inputStyle, width: 100 }}
                />
                <button onClick={() => removeCharge(idx)} style={{ ...btnTextDanger, fontSize: 12 }}>Remove</button>
              </div>
            ))}
            <button onClick={addCharge} style={{ ...btnText, fontSize: 12 }}>+ Add charge</button>
          </div>

          {/* Totals */}
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e2e8f0', fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b' }}>Products subtotal</span>
              <span>${productsTotal.toFixed(2)}</span>
            </div>
            {chargesTotal > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <span style={{ color: '#64748b' }}>Charges</span>
                <span>${chargesTotal.toFixed(2)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontWeight: 700, fontSize: 14 }}>
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>

          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <button onClick={onRemove} style={{ ...btnTextDanger, fontSize: 12 }}>
              Remove this PO from the batch
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SupportingDocs: general multi-file attachment (proofs, spec sheets) ────
// Unlike the per-art (decorator) or per-location (supplier) art controls,
// this is a plain list — add as many files as needed, each independently
// removable. No "replace" concept since there's no single slot to replace;
// just remove the old one and add a new one if needed.
function SupportingDocs({ files, onChange, getToken, showToast, clientId }) {
  const [uploading, setUploading] = useState(false);

  const addFile = async (fileList) => {
    const file = fileList && fileList[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = await getToken();
      const uploaded = await uploadFile(file, { kind: 'other', clientId, label: file.name }, token);
      onChange([
        ...files,
        {
          art_file_id: uploaded.id,
          art_file_name: uploaded.filename || file.name,
          art_file_url: uploaded.url || null,
          art_file_mime: uploaded.mime_type || file.type || null,
        },
      ]);
      showToast?.(uploaded.reused ? 'File already on hand — reused existing upload' : 'File attached');
    } catch (e) {
      showToast?.('Upload failed: ' + e.message, 'error');
    } finally {
      setUploading(false);
    }
  };
  const removeFile = (idx) => onChange(files.filter((_, i) => i !== idx));

  return (
    <div style={{ marginTop: 16 }}>
      <div style={labelStyle}>Supporting documents</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
        Proofs, spec sheets, client approvals — anything general that isn't tied to one specific location or garment.
      </div>
      {files.map((f, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          {f.art_file_mime && f.art_file_mime.startsWith('image/') ? (
            <a href={f.art_file_url} target="_blank" rel="noreferrer">
              <img src={f.art_file_url} alt={f.art_file_name || 'File'}
                style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'contain', border: '1px solid #E2E8F0', background: '#fff', padding: 1 }} />
            </a>
          ) : null}
          <a href={f.art_file_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: G, textDecoration: 'none' }}>
            📎 {f.art_file_name || 'file'}
          </a>
          <button onClick={() => removeFile(idx)} style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
            Remove
          </button>
        </div>
      ))}
      <label style={{ fontSize: 12, color: G, cursor: 'pointer', fontWeight: 500, display: 'inline-block' }}>
        {uploading ? 'Uploading…' : '📎 Add supporting document'}
        <input type="file" style={{ display: 'none' }} disabled={uploading}
          onChange={e => addFile(e.target.files)} />
      </label>
    </div>
  );
}

// ─── DecoratorItems: read-only decoration breakdown for a decorator PO ───────
// Groups garments Location → Art → Ink. Identical items collapse together; an
// ink group after the first under an art is a color change (same screens, new
// ink). No blank pricing — decoration cost only.
function groupByArtInk(garments) {
  const byArt = [];
  for (const g of garments || []) {
    const artKey = g.art || 'Art';
    let a = byArt.find(x => x.art === artKey);
    if (!a) { a = { art: artKey, inkGroups: [] }; byArt.push(a); }
    const inkKey = g.ink || '';
    let ig = a.inkGroups.find(x => x.ink === inkKey);
    if (!ig) { ig = { ink: inkKey, garments: [] }; a.inkGroups.push(ig); }
    ig.garments.push(g);
  }
  byArt.forEach(a => a.inkGroups.forEach((ig, i) => { ig.isColorChange = i > 0; }));
  return byArt;
}

function DecoratorItems({ draft, onChange, getToken, showToast, clientId }) {
  const lbl = { fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' };
  const [uploadingKey, setUploadingKey] = useState(null); // `${diIndex}-${artKey}` while a PUT is in flight

  // Art is attached at the ART-GROUP level (not per garment) — every garment
  // sharing an art label shares the same artwork/screens, so the file
  // reference is written onto all of them together, keeping them consistent.
  const applyArtToGroup = (diIndex, artKey, patch) => {
    const nextDecoItems = draft.deco_items.map((di, i) => {
      if (i !== diIndex) return di;
      return {
        ...di,
        garments: di.garments.map(g => (g.art || 'Art') === artKey ? { ...g, ...patch } : g),
      };
    });
    onChange({ deco_items: nextDecoItems });
  };

  const attachArt = async (diIndex, artKey, fileList, replacesFileId) => {
    const file = fileList && fileList[0];
    if (!file) return;
    const key = `${diIndex}-${artKey}`;
    setUploadingKey(key);
    try {
      const token = await getToken();
      const uploaded = await uploadFile(file, { kind: 'art', clientId, label: file.name, replacesFileId }, token);
      applyArtToGroup(diIndex, artKey, {
        art_file_id: uploaded.id,
        art_file_name: uploaded.filename || file.name,
        art_file_url: uploaded.url || null,
        art_file_mime: uploaded.mime_type || file.type || null,
      });
      showToast?.(uploaded.reused ? 'Art already on file — reused existing upload' : 'Art attached');
    } catch (e) {
      showToast?.('Art upload failed: ' + e.message, 'error');
    } finally {
      setUploadingKey(null);
    }
  };

  const removeArt = (diIndex, artKey) => {
    applyArtToGroup(diIndex, artKey, { art_file_id: null, art_file_name: null, art_file_url: null, art_file_mime: null });
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ ...lbl, marginBottom: 10 }}>Decoration to run</div>
      {(draft.deco_items || []).map((d, i) => (
        <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 10, background: '#f8fafc' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>{d.spec}</div>
          <div style={{ fontSize: 12, color: '#475569', marginBottom: 10 }}>
            {d.multi_location ? `${d.impressions} prints (${d.combined_qty} pcs × ${(d.locations||[]).length||2} locations)` : `${d.combined_qty} pieces`} · {d.run_rate != null ? `$${Number(d.run_rate).toFixed(2)}/${d.multi_location ? 'print' : 'pc'}` : '—'} = <strong style={{ color: '#0f172a' }}>${Number(d.run_total || 0).toFixed(2)}</strong>
            {d.setup_total > 0 ? <> · setup <strong style={{ color: '#0f172a' }}>${Number(d.setup_total).toFixed(2)}</strong></> : null}
            {(d.art_count || d.color_changes) ? <span style={{ color: '#94a3b8' }}> · {d.art_count || 1} art{(d.art_count || 1) === 1 ? '' : 's'}{d.color_changes ? `, ${d.color_changes} color change${d.color_changes === 1 ? '' : 's'}` : ''}</span> : null}
            {/* Per-job total, matching DecoJobDisplay in PODetail.jsx. Setup is
                no longer a pooled line at the bottom of the PO, so each imprint
                has to total itself here. NOTE: this component duplicates that
                summary — a change to one needs the same change in the other. */}
            <> · total deco cost = <strong style={{ color: '#0f172a' }}>${(Number(d.run_total || 0) + Number(d.setup_total || 0)).toFixed(2)}</strong></>
          </div>
          {d.print_colors ? (
            <div style={{ fontSize: 12, color: '#334155', marginBottom: 10, padding: '6px 10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6 }}>
              <span style={{ ...lbl }}>Print colors:</span> <strong>{d.print_colors}</strong>
            </div>
          ) : null}
          {Array.isArray(d.locations) && d.locations.length > 1 ? (
            <div style={{ fontSize: 12, color: '#334155', marginBottom: 10, padding: '6px 10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6 }}>
              <span style={{ ...lbl }}>Artwork printed on:</span> <strong>{d.locations.join(', ')}</strong> <span style={{ color: '#94a3b8' }}>· same artwork each location</span>
            </div>
          ) : null}
          <div style={{ ...lbl, marginBottom: 4 }}>Garments to decorate (check in on arrival)</div>
          {groupByArtInk(d.garments).map((a, ai) => {
            const artFile = a.inkGroups.flatMap(ig => ig.garments).find(g => g.art_file_id || g.art_file_url);
            const uploading = uploadingKey === `${i}-${a.art}`;
            return (
            <div key={ai} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', marginBottom: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <span>{a.art}<span style={{ fontSize: 10, fontWeight: 400, color: '#94a3b8' }}> · setup</span></span>
                {artFile ? (
                  <>
                    {/* url may be absent (presign failed, or an older record
                        saved before confirm returned one). The file is still
                        attached — show the name either way, and only render a
                        thumbnail/link when there's something to point at. */}
                    {artFile.art_file_url && artFile.art_file_mime && artFile.art_file_mime.startsWith('image/') ? (
                      <a href={artFile.art_file_url} target="_blank" rel="noreferrer">
                        <img src={artFile.art_file_url} alt={artFile.art_file_name || 'Art'}
                          style={{ width: 26, height: 26, borderRadius: 4, objectFit: 'contain', border: '1px solid #E2E8F0', background: '#fff', padding: 1 }} />
                      </a>
                    ) : null}
                    {artFile.art_file_url ? (
                      <a href={artFile.art_file_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 500, color: G, textDecoration: 'none' }}>
                        📎 {artFile.art_file_name || 'art file'}
                      </a>
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 500, color: '#475569' }}>
                        📎 {artFile.art_file_name || 'art file'}
                      </span>
                    )}
                    <label style={{ fontSize: 11, color: '#64748b', cursor: 'pointer', textDecoration: 'underline', fontWeight: 400 }}>
                      Replace
                      <input type="file" accept="image/*,.pdf,.ai,.eps,.svg" style={{ display: 'none' }}
                        onChange={e => attachArt(i, a.art, e.target.files, artFile.art_file_id)} />
                    </label>
                    <button onClick={() => removeArt(i, a.art)}
                      style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 11, fontWeight: 400, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
                      Remove
                    </button>
                  </>
                ) : (
                  <label style={{ fontSize: 11, color: G, cursor: 'pointer', fontWeight: 500 }}>
                    {uploading ? 'Uploading…' : '📎 Attach art file'}
                    <input type="file" accept="image/*,.pdf,.ai,.eps,.svg" style={{ display: 'none' }} disabled={uploading}
                      onChange={e => attachArt(i, a.art, e.target.files, null)} />
                  </label>
                )}
              </div>
              {a.inkGroups.map((ig, ii) => (
                <div key={ii} style={{ marginBottom: 4 }}>
                  {ig.isColorChange && (
                    <div style={{ fontSize: 10, color: '#b45309', fontWeight: 600, margin: '3px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ flex: 1, borderTop: '1px dashed #fcd34d' }} /> color change <span style={{ flex: 1, borderTop: '1px dashed #fcd34d' }} />
                    </div>
                  )}
                  {ig.ink ? <div style={{ fontSize: 11, color: '#475569', fontWeight: 500, marginBottom: 2, paddingLeft: 8 }}>{ig.ink} ink</div> : null}
                  {ig.garments.map((g, gi) => (
                    <div key={gi} style={{ fontSize: 12, color: '#334155', marginBottom: 2, paddingLeft: 8 }}>
                      {g.item_no != null ? <strong>#{g.item_no} </strong> : null}{g.name}{g.color ? <span style={{ color: '#475569' }}> · {g.color}</span> : null} — {g.qty} pcs
                      {Array.isArray(g.sizes) && g.sizes.length ? <span style={{ color: '#94a3b8' }}> ({g.sizes.map(s => `${s.label}:${s.qty}`).join(', ')})</span> : null}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            );
          })}
        </div>
      ))}

      <div style={{ marginTop: 8 }}>
        <div style={{ ...lbl, marginBottom: 4 }}>Notes to decorator (prints on the PO)</div>
        <textarea
          value={draft.decoration_notes || ''}
          onChange={e => onChange && onChange({ decoration_notes: e.target.value })}
          placeholder="Art placement, PMS colors, ink notes, check-in / in-hand instructions…"
          style={{ width: '100%', minHeight: 64, border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontFamily: 'inherit', color: '#0f172a', boxSizing: 'border-box', resize: 'vertical', outline: 'none' }}
        />
      </div>

      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
        Blanks drop-ship to the decorator; this PO covers decoration only — no garment pricing.
      </div>
    </div>
  );
}

// ─── ItemEditor: per-product color, mockup, decorations ──────────────────────
function ItemEditor({ item, onChange, getToken, showToast, clientId }) {
  const decorations = Array.isArray(item.decorations) ? item.decorations : [];
  const canAddMore = decorations.length < 4;

  const addDecoration = () => {
    if (!canAddMore) return;
    onChange({
      decorations: [
        ...decorations,
        { location: '', method: '', colors: '', size: '', artwork_filename: '', notes: '' },
      ],
    });
  };
  const updateDecoration = (idx, patch) => {
    const next = decorations.map((d, i) => i === idx ? { ...d, ...patch } : d);
    onChange({ decorations: next });
  };
  const removeDecoration = (idx) => {
    onChange({ decorations: decorations.filter((_, i) => i !== idx) });
  };

  // Wrapper so qty / unit_cost edits always recompute line_total. Keeps
  // the data internally consistent regardless of which field changed.
  const updatePricing = (patch) => {
    const next = { ...item, ...patch };
    const qty = Number(next.qty) || 0;
    const unit = Number(next.unit_cost) || 0;
    next.line_total = +(qty * unit).toFixed(2);
    onChange(next);
  };

  // Apparel (SanMar/S&S) blank: show the per-size grid, hide decoration (that
  // goes to the decorator PO). Regular promo items are unchanged.
  const isApparel = !!item.apparel && Array.isArray(item.sizes);

  return (
    <div style={{
      border: '1px solid #e2e8f0', borderRadius: 6, padding: 12, marginBottom: 10,
      background: '#fafbfc',
    }}>
      {/* Item header line — name + qty/unit/total, or a size grid for apparel */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{item.name}</div>
        {isApparel ? (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead><tr>
              {['Size','Qty','Unit cost','Line'].map(h=>(
                <th key={h} style={{ textAlign: h==='Size'?'left':'right', padding:'4px 6px', fontSize:10, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.5px', borderBottom:'1px solid #e2e8f0' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{item.sizes.map((s, si) => (
              <tr key={si} style={{ borderBottom:'1px solid #f1f5f9' }}>
                <td style={{ padding:'4px 6px', fontWeight:600 }}>{s.label}</td>
                <td style={{ padding:'4px 6px', textAlign:'right' }}>{s.qty}</td>
                <td style={{ padding:'4px 6px', textAlign:'right' }}>${Number(s.cost||0).toFixed(2)}</td>
                <td style={{ padding:'4px 6px', textAlign:'right', fontWeight:600 }}>${(Number(s.cost||0)*Number(s.qty||0)).toFixed(2)}</td>
              </tr>
            ))}</tbody>
            <tfoot><tr style={{ borderTop:'2px solid #e2e8f0' }}>
              <td style={{ padding:'5px 6px', fontWeight:600 }}>Total</td>
              <td style={{ padding:'5px 6px', textAlign:'right', fontWeight:600 }}>{item.qty}</td>
              <td/>
              <td style={{ padding:'5px 6px', textAlign:'right', fontWeight:600, color:'#0f172a' }}>${Number(item.line_total||0).toFixed(2)}</td>
            </tr></tfoot>
          </table>
        ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <div>
            <div style={labelStyle}>Quantity</div>
            <input
              type="number"
              min="1"
              step="1"
              value={item.qty || ''}
              onChange={e => updatePricing({ qty: Math.max(0, parseInt(e.target.value, 10) || 0) })}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={labelStyle}>Unit cost</div>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#94a3b8' }}>$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={item.unit_cost ?? ''}
                onChange={e => updatePricing({ unit_cost: Math.max(0, Number(e.target.value) || 0) })}
                style={{ ...inputStyle, paddingLeft: 22 }}
              />
            </div>
          </div>
          <div>
            <div style={labelStyle}>Line total</div>
            <div style={{
              ...inputStyle,
              background: '#f1f5f9',
              color: '#0f172a',
              fontWeight: 600,
              cursor: 'not-allowed',
            }}>
              ${Number(item.line_total || 0).toFixed(2)}
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Color + mockup (mockup only for non-apparel — apparel decoration is on the decorator PO) */}
      <div style={{ display: 'grid', gridTemplateColumns: isApparel ? '1fr' : '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <ColorPicker
          color={item.color || ''}
          options={item.available_colors || []}
          onChange={(v) => onChange({ color: v })}
        />
        {!isApparel && (
        <div>
          <div style={labelStyle}>Mockup filename</div>
          <input
            placeholder="e.g. tote-mockup.pdf"
            value={item.mockup_filename || ''}
            onChange={e => onChange({ mockup_filename: e.target.value })}
            style={inputStyle}
          />
        </div>
        )}
      </div>

      {/* Decorations — not on blank apparel POs (those go to the decorator) */}
      {!isApparel && (
      <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 10, marginTop: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={labelStyle}>Decoration locations ({decorations.length}/4)</div>
          {canAddMore && (
            <button onClick={addDecoration} style={{ ...btnText, fontSize: 12 }}>
              + Add location
            </button>
          )}
        </div>

        {decorations.length === 0 && (
          <div style={{
            padding: '12px 14px', background: '#fff', border: '1px dashed #cbd5e1',
            borderRadius: 6, fontSize: 12, color: '#94a3b8', textAlign: 'center',
          }}>
            No decoration on this item. Click "Add location" if needed.
          </div>
        )}

        {decorations.map((deco, idx) => (
          <DecorationEditor
            key={idx}
            deco={deco}
            onChange={(patch) => updateDecoration(idx, patch)}
            onRemove={() => removeDecoration(idx)}
            getToken={getToken} showToast={showToast} clientId={clientId}
          />
        ))}
      </div>
      )}
    </div>
  );
}

// ─── DecorationEditor: one location's worth of decoration details ────────────
function DecorationEditor({ deco, onChange, onRemove, getToken, showToast, clientId }) {
  const [uploading, setUploading] = useState(false);

  const attachArt = async (fileList, replacesFileId) => {
    const file = fileList && fileList[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = await getToken();
      const uploaded = await uploadFile(file, { kind: 'art', clientId, label: file.name, replacesFileId }, token);
      onChange({
        art_file_id: uploaded.id,
        art_file_name: uploaded.filename || file.name,
        art_file_url: uploaded.url || null,
        art_file_mime: uploaded.mime_type || file.type || null,
        // Keep the legacy text field in sync so anything still reading it
        // (older saved POs, the PDF's fallback text) shows something sane.
        artwork_filename: uploaded.filename || file.name,
      });
      showToast?.(uploaded.reused ? 'Art already on file — reused existing upload' : 'Art attached');
    } catch (e) {
      showToast?.('Art upload failed: ' + e.message, 'error');
    } finally {
      setUploading(false);
    }
  };
  const removeArt = () => {
    onChange({ art_file_id: null, art_file_name: null, art_file_url: null, art_file_mime: null });
  };

  return (
    <div style={{
      border: '1px solid #d4d4d8', borderRadius: 6, padding: 12, marginBottom: 8, background: '#fff',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <input
          placeholder="Location (e.g. Front, Back, Left chest, Sleeve)"
          value={deco.location || ''}
          onChange={e => onChange({ location: e.target.value })}
          style={{ ...inputStyle, flex: 1, fontWeight: 600 }}
        />
        <button onClick={onRemove} style={{ ...btnTextDanger, fontSize: 12 }}>Remove</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
        <div>
          <div style={labelStyle}>Imprint method</div>
          <input
            placeholder="e.g. Screen print, Embroidery, Laser, Heat transfer"
            value={deco.method || ''}
            onChange={e => onChange({ method: e.target.value })}
            style={inputStyle}
          />
        </div>
        <div>
          <div style={labelStyle}>Imprint colors</div>
          <input
            placeholder="e.g. PMS 286, White, Black"
            value={deco.colors || ''}
            onChange={e => onChange({ colors: e.target.value })}
            style={inputStyle}
          />
        </div>
        <div>
          <div style={labelStyle}>Imprint size</div>
          <input
            placeholder='e.g. 4" wide, 2.5" x 1"'
            value={deco.size || ''}
            onChange={e => onChange({ size: e.target.value })}
            style={inputStyle}
          />
        </div>
        <div>
          <div style={labelStyle}>Artwork file</div>
          {deco.art_file_id ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', paddingTop: 4 }}>
              {deco.art_file_mime && deco.art_file_mime.startsWith('image/') ? (
                <a href={deco.art_file_url} target="_blank" rel="noreferrer">
                  <img src={deco.art_file_url} alt={deco.art_file_name || 'Art'}
                    style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'contain', border: '1px solid #E2E8F0', background: '#fff', padding: 1 }} />
                </a>
              ) : null}
              <a href={deco.art_file_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: G, textDecoration: 'none' }}>
                📎 {deco.art_file_name || deco.artwork_filename || 'art file'}
              </a>
              <label style={{ fontSize: 11, color: '#64748b', cursor: 'pointer', textDecoration: 'underline' }}>
                Replace
                <input type="file" accept="image/*,.pdf,.ai,.eps,.svg" style={{ display: 'none' }}
                  onChange={e => attachArt(e.target.files, deco.art_file_id)} />
              </label>
              <button onClick={removeArt} style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
                Remove
              </button>
            </div>
          ) : (
            <label style={{ fontSize: 12, color: G, cursor: 'pointer', fontWeight: 500, display: 'inline-block', paddingTop: 6 }}>
              {uploading ? 'Uploading…' : '📎 Attach artwork file'}
              <input type="file" accept="image/*,.pdf,.ai,.eps,.svg" style={{ display: 'none' }} disabled={uploading}
                onChange={e => attachArt(e.target.files, null)} />
            </label>
          )}
        </div>
      </div>

      <div>
        <div style={labelStyle}>Notes for this location</div>
        <textarea
          placeholder="Any special instructions for this decoration..."
          value={deco.notes || ''}
          onChange={e => onChange({ notes: e.target.value })}
          rows={2}
          style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
        />
      </div>
    </div>
  );
}

// ─── ColorPicker: dropdown of catalog colors + freeform fallback ─────────────
function ColorPicker({ color, options, onChange }) {
  // Determine if the current color is one of the catalog options
  const optionNames = options.map(o => o.name);
  const isCustom = color && !optionNames.includes(color);
  const [mode, setMode] = useState(isCustom || options.length === 0 ? 'custom' : 'preset');

  return (
    <div>
      <div style={labelStyle}>Color</div>
      {mode === 'preset' && options.length > 0 ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <select
            value={color}
            onChange={e => {
              if (e.target.value === '__custom__') {
                setMode('custom');
                onChange('');
              } else {
                onChange(e.target.value);
              }
            }}
            style={{ ...inputStyle, flex: 1, padding: '8px 10px' }}
          >
            <option value="">Select color…</option>
            {options.map((opt, i) => (
              <option key={i} value={opt.name}>{opt.name}</option>
            ))}
            <option value="__custom__">— Custom (type in) —</option>
          </select>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            placeholder="Type color name"
            value={color}
            onChange={e => onChange(e.target.value)}
            style={{ ...inputStyle, flex: 1 }}
          />
          {options.length > 0 && (
            <button
              onClick={() => { setMode('preset'); onChange(''); }}
              style={{ ...btnSecondary, padding: '6px 10px', fontSize: 11 }}
              title="Switch to dropdown"
            >
              ⌄
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Tiny shared bits ─────────────────────────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1',
  borderRadius: 6, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box',
};
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 };

function Field({ label, value, onChange, type = 'text', fullWidth, maxLength }) {
  return (
    <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
      <div style={labelStyle}>{label}</div>
      <input
        type={type}
        value={value || ''}
        maxLength={maxLength}
        onChange={e => onChange(e.target.value)}
        style={inputStyle}
      />
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div style={{
      fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: 0.5, color: '#64748b',
      marginTop: 20, marginBottom: 12,
    }}>{title}</div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  zIndex: 1000, padding: '40px 20px', overflowY: 'auto',
};
const modalStyle = {
  background: '#fff', borderRadius: 12, width: '100%', maxWidth: 800,
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};
const modalHeader = {
  padding: '20px 24px', borderBottom: '1px solid #e2e8f0',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};
const modalBody = { padding: 24 };
const closeBtn = {
  border: 'none', background: 'transparent', fontSize: 28,
  cursor: 'pointer', color: '#94a3b8', lineHeight: 1, padding: 0,
};
const errorBox = {
  background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b',
  padding: '10px 14px', borderRadius: 6, fontSize: 13, marginBottom: 12,
};
const gridTwo = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const btnPrimary = {
  padding: '10px 20px', background: G, color: '#fff', border: 'none',
  borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const btnSecondary = {
  padding: '10px 20px', background: '#fff', color: '#334155',
  border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14,
  fontWeight: 600, cursor: 'pointer',
};
const btnText = {
  background: 'none', border: 'none', color: G, padding: 4,
  cursor: 'pointer', fontWeight: 600,
};
const btnTextDanger = {
  background: 'none', border: 'none', color: '#dc2626', padding: 4,
  cursor: 'pointer', fontWeight: 600,
};
